import {ICategory} from 'app/main/shared/shared/table/components/category-name/category-name.component';
import {IAuditWithUserInterface} from '../../../global/audit.interface';
import {ContractControl} from '../documents/dto/document-details.dto';
import {IDocumentTypesDto} from '../documents/dto/document-listing.dto';
import {IIMetadataItemOptionsDto} from '../settings/documentfields/metadata-item.dto';
import {UserStatusEnum} from '../users/userslisting/store/user-status.enum';
import {IConversionRatesResponse, IMultisectionGetResponse} from './contract-control.service';
import {IProgressablePeriodRenewal} from './store/dto/contract-control-view';
import {IGetCategoriesItemDto} from '../categories.dto';


export interface IContractMonthlyCost {
  MRC: number;
  ARC: number;
  TCC: number;
  OneTime: number;
}

export interface IContractMonthlyRevenue {
  MRR: number;
  ARR: number;
  TCR: number;
  OneTime: number;
}

export interface IContractControlData {
  id: number;
  user_id: number;
  workspace_id: number;
  contract_control_name: string;
  document_type_id: number;
  category_ids: number | number[];
  organization_id: number;
  contract_control_image: string;
  contract_no: string;
  is_active: number;
  contract_type: number;
  parent_contract: number;
  original_import_file_name: string;
  created_at: string;
  updated_at: string;
  filter_range: string;
  status: string;
  contacts: IContact[];
  managers: IManager[];
  documents: Document[];
  related_contract: RelatedContract[];
  organization: Organization;
  category: ICategory;
  sub_contracts: SubContract[];
  master_contract: IMasterContract;
  period_renewal: PeriodRenewal | IProgressablePeriodRenewal;
  costs: Cost[];
  revenues: IRevenueDTO[];
  monthly_cost?: IContractMonthlyCost;
  monthly_revenue?: IContractMonthlyRevenue;
  note: IContractControlNote[];
  todo: Todo[];
  document_type: IDocumentTypesDto;
  custom_boxes_data: ICustomBoxesDaum[];
  categories?: IGetCategoriesItemDto[];
  category_groups?: { path: string, leaf: string }[];
  group?: any;
}

export interface ISearchContract extends Partial<IMultisectionGetResponse> {}

export interface IMasterContract {
  id: number;
  user_id: number;
  workspace_id: number;
  contract_control_name: string;
  document_type_id: number;
  category_ids: number | number[];
  organization_id: number;
  contract_control_image: string;
  contract_no: string;
  is_active: number;
  contract_type: number;
  parent_contract: number;
  original_import_file_name: string;
  created_at: string;
  updated_at: string;
  filter_range: string;
}

export interface IContact {
  id: number;
  user_id: number;
  workspace_id: number;
  contract_control_id: number;
  created_at: string;
  updated_at: string;
  sectionable_type: string;
  sectionable_id: number;
  sectionable: Sectionable;
}

export interface Sectionable {
  id: number;
  user_id: number;
  workspace_id: number;
  organization_id: number;
  firstname: string;
  lastname: string;
  email: string;
  phone_no: string;
  persnol_no: string;
  address: string;
  zipcode: string;
  city: string;
  profile_image: string;
  created_at: string;
  updated_at: string;
}

export interface IManager {
  id: number;
  user_id: number;
  workspace_id: number;
  contract_control_id: number;
  created_at: string;
  updated_at: string;
  sectionable_type: string;
  sectionable_id: number;
  sectionable: Sectionable2;
}

export interface Sectionable2 {
  id: number;
  tenant_owner?: boolean;
  name: string;
  email?: string;
  status?: string;
  phone: string;
  profile_image: string;
  signature: string;
  login_via?: string;
  default?: number;
  predefined?: number;
}

export interface IDocument extends IAuditWithUserInterface {
  id: number;
  user_id: number;
  workspace_id: number;
  contract_control_id: number;
  created_at: string;
  updated_at: string;
  sectionable_type: string;
  sectionable_id: number;
  sectionable: IDocumentSectionable;
}

export interface IDocumentSectionable {
  id: number;
  user_id: number;
  workspace_id: number;
  template_id?: number;
  category_id?: number;
  document_type_id?: number;
  name: string;
  content?: string;
  status: string;
  status_updated_at: string;
  visible_name: string;
  language: string;
  signing_deadline?: number;
  signing_reminder: string;
  recurring: number;
  invitation_message: string;
  confirmation_message: string;
  document_key: number;
  dk_type: string;
  dk_password: string;
  dk_password_hint: string;
  dk_send_via_sms: number;
  save_to_folder: number;
  allow_forward: number;
  allow_chat: number;
  attach_video: number;
  video_type: string;
  video_url: string;
  video_title: string;
  document_price: number;
  document_number: string;
  pdf_path: string;
  visibility: string;
  crm_data?: CrmData;
  timezone?: Timezone;
  created_at: string;
  updated_at: string;
  deleted_at: string;
  thumbnail: string;
  due_date: string;
  user: User;
}

export interface CrmData {
  type: string;
  deal_id: string;
  provider: string;
}

export interface Timezone {
  timezone: string;
  date_format: string;
  hours_format: string;
  month_format: number;
}

export interface RelatedContract extends IAuditWithUserInterface {
  id: number;
  workspace_id: number;
  contract_control_id: number;
  related_contract_id: number;
}

export interface GetRelatedContract extends RelatedContract {
  contract_control: ContractControl;
}

export interface Organization {
  id: number;
  user_id: number;
  workspace_id: number;
  name: string;
  register_no: string;
  address: string;
  zipcode: string;
  city: string;
  phone: string;
  organization_image: string;
  created_at: string;
  updated_at: string;
}

export interface Category {
  id: number;
  user_id: number;
  workspace_id: number;
  category_name: string;
  created_at: string;
  updated_at: string;
  parent_category: number;
}

export interface SubContract {
  id: number;
  user_id: number;
  workspace_id: number;
  contract_control_name: string;
  document_type_id: number;
  category_id: number;
  organization_id: number;
  contract_control_image: string;
  contract_no: string;
  is_active: number;
  contract_type: number;
  parent_contract: number;
  original_import_file_name: string;
  created_at: string;
  updated_at: string;
  filter_range: string;
}

export interface IPeriodRenewalExpirationDates {
  expire: string;
  auto_renew: string;
  todo: string[]
}

export interface PeriodRenewal {
  id: number;
  user_id: number;
  workspace_id: number;
  document_type_id: number;
  contract_control_id: number;
  start_date: string;
  end_date: string;
  contract_length: number;
  notice_date: string;
  notice: number;
  notice_period: string;
  auto_renew: number;
  expiration_dates: IPeriodRenewalExpirationDates;
  contract_length_renew: string;
  notice_renew: number;
  notice_period_renew: string;
  last_auto_date_renew?: string;
  auto_date_renew: string;
  notice_date_renew: string;
  progress?: number;
  other_length_renew: string;
  other_length_period_renew: string;
  created_at: string;
  updated_at: string;
}

export interface Cost extends IAuditWithUserInterface {
  contract_control_id: number;
  conversion_date: string;
  conversion_rate: string;
  description: string;
  billing_frequency: string;
  billing_start: string;
  billing_end: string;
  cost_amount: number;
  cost_currency: string;
  has_billing_end_date?: boolean;
  cost_period: string;
  cost_converted_amount: number;
  created_at: string;
  document_type_id: number;
  id: number;
  updated_at: string;
  user_id: number;
  workspace_id: number;
}

export interface IRevenueDTO extends IConversionRatesResponse {
  id: number;
  user_id: number;
  workspace_id: number;
  document_type_id: number;
  contract_control_id: number;
  description: string;
  revenue_amount: number;
  billing_frequency: string;
  revenue_currency: string;
  has_billing_end_date: boolean;
  billing_start: string;
  billing_end: string;
  created_at: string;
  updated_at: string;
}

export interface Todo {
  id: number;
  user_id: number;
  workspace_id: number;
  contract_control_id: number;
  description: string;
  due_date: string;
  status: number;
  created_at: string;
  updated_at: string;
  todo_user: TodoUser[];
}

export interface TodoUser {
  id: number;
  todo_id: number;
  userable_type: string;
  userable_id: number;
  created_at: string;
  updated_at: string;
  userable: Userable;
}

export interface Userable {
  id: number;
  tenant_owner: boolean;
  name: string;
  email: string;
  status: string;
  phone: string;
  profile_image: string;
  signature: string;
  login_via: string;
}

export interface DocumentType {
  id: number;
  user_id: number;
  workspace_id: number;
  document_type: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  document_type_data: DocumentTypeData;
}

export interface DocumentTypeData extends IAuditWithUserInterface {
  id: number;
  document_type_id: number;
  status: number;
  users: number;
  cost: number;
  period_renewal: number;
  revenue: number;
  manager: number;
  attach_document: number;
  audit_trail: number;
  todo_list: number;
  linked_contracts: number;
  contacts: number;
  comments: number;
  contract_events: number;
  created_at: string;
  updated_at: string;
}

export interface ICustomBoxesDaum {
  id?: number
  custom_boxes_data_id?: number
  contract_control_id?: number
  name?: string
  order?: number
  value?: string | number
  created_at?: string
  updated_at?: string
  document_type_custom_boxes_data?: DocumentTypeCustomBoxesData[]
}

export interface DocumentTypeCustomBoxesDataSub {
  contract_control_id: number;
  custom_boxes_data_id: number;
  id: number;
  order: number;
  value: string | number;
}

export interface DocumentTypeCustomBoxesData {
  contract_control_custom_boxes_data?: DocumentTypeCustomBoxesDataSub;
  id?: number;
  user_id?: number;
  workspace_id?: number;
  document_type_custom_boxe_id?: number;
  meta_data_id?: number;
  status?: number;
  created_at?: string;
  updated_at?: string;
  field_order?: number;
  document_type_custom_boxes?: DocumentTypeCustomBoxes;
  meta_data?: MetaData;
}

export interface DocumentTypeCustomBoxes {
  id: number;
  document_type_data_id: number;
  status: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface MetaData {
  id: number;
  user_id: number;
  workspace_id: number;
  meta_data_name: string;
  meta_data_type: string;
  section: string;
  api_attribute: string;
  source: string;
  is_required: number;
  meta_data_description: string;
  created_at: string;
  updated_at: string;
  meta_data_option: IIMetadataItemOptionsDto[];
}

export interface IContractControlNote {
  id: number;
  user_id: number;
  workspace_id: number;
  document_type_id: number;
  contract_control_id: number;
  comment: string;
  created_at: string;
  updated_at: string;
  user: User;
}

export interface User {
  email: string;
  id: number;
  login_via: string;
  name: string;
  phone?: string;
  profile_image: string;
  signature: string;
  status: UserStatusEnum;
  tenant_owner: boolean;
}

export enum ReminderModalSection {
  CC = 'ContractControl',
  Addon = 'Addon'
}
